import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    styled,
    TextField
} from "@mui/material";

import { useState } from "react";
import { useTarefa } from "../../../../contexts/TarefaContext";
import AgendamentoMensal from "./Mensal";
import AgendamentoSemanal from "./Semanal";
import AgendamentoRepetidamente from "./Repetidamente";
import { v4 as uuidv4 } from 'uuid';
import { IAgendamentoMensal, IAgendamentoRepedidamente, IAgendamentoSemalnamente, IAgendamentoTarefa } from "../../../../types/tarefa";

const BFInput = styled(TextField)({
    marginTop: '16px'
});

const Agendamento = () => {
    const [tarefa, setTarefa] = useTarefa();

    const getInitDataInicio = () => {
        if (tarefa.dataInicio) {
            const d = new Date(tarefa.dataInicio);
            return isNaN(d.getTime()) ? "" : d.toISOString().split('T')[0];
        }
        return "";
    };

    const getInitDataFim = () => {
        if (tarefa.dataFim) {
            const d = new Date(tarefa.dataFim);
            return isNaN(d.getTime()) ? "" : d.toISOString().split('T')[0];
        }
        return "";
    };

    const [tipo, setTipo] = useState<string>(tarefa.agendamento?.tipo || "Diariamente");
    const [dataInicio, setDataInicio] = useState(getInitDataInicio());
    const [dataFim, setDataFim] = useState(getInitDataFim());
    const [horaExecucao, setHoraExecucao] = useState(tarefa.agendamento?.horaExecucao || "");

    const getFormAgendamento = (tipoAgendamento: string) => {
        if (tipoAgendamento === "Semanalmente") {
            return <AgendamentoSemanal />;
        } else if (tipoAgendamento === "Mensalmente") {
            return <AgendamentoMensal />;
        } else if (tipoAgendamento === "Repetidamente") {
            return <AgendamentoRepetidamente />;
        }
        return <></>;
    };

    const [formTipoAgendamento, setFormTipoAgendamento] = useState<JSX.Element>(
        getFormAgendamento(tarefa.agendamento?.tipo || "Diariamente")
    );

    function handleHoraExecucaoAlterado(event: React.ChangeEvent<HTMLInputElement>): void {
        event.preventDefault();
        setHoraExecucao(event.target.value);        
        if (tarefa.agendamento) {
            setTarefa({
                ...tarefa, agendamento: {
                    ...tarefa.agendamento,
                    horaExecucao: event.target.value
                }
            });                       
        }
    }

    function handleDataInicioAlterada(event: React.ChangeEvent<HTMLInputElement>): void {
        event.preventDefault();
        setTarefa({ ...tarefa, dataInicio: new Date(event.target.value) });
        setDataInicio(event.target.value);
    }

    function handleDataFimAlterada(event: React.ChangeEvent<HTMLInputElement>): void {
        event.preventDefault();
        setTarefa({ ...tarefa, dataFim: new Date(event.target.value) });
        setDataFim(event.target.value);
    }

    function handleChangeTipo(event: SelectChangeEvent) {
        event.preventDefault();
        const novoTipo = event.target.value;

        if (novoTipo === "Semanalmente") {
            setFormTipoAgendamento(<AgendamentoSemanal />);
            const agendamentoSemanal: IAgendamentoSemalnamente = {
                id: uuidv4(),
                tipo: novoTipo,
                dias: (tarefa.agendamento as any)?.dias || [],
                horaExecucao: horaExecucao
            };
            setTarefa({ ...tarefa, agendamento: agendamentoSemanal });
        } else if (novoTipo === "Mensalmente") {
            setFormTipoAgendamento(<AgendamentoMensal />);
            const agendamentoMensal: IAgendamentoMensal = {
                id: uuidv4(),
                tipo: novoTipo,
                dias: (tarefa.agendamento as any)?.dias || [],
                meses: (tarefa.agendamento as any)?.meses || [],
                horaExecucao: horaExecucao
            };
            setTarefa({ ...tarefa, agendamento: agendamentoMensal });
        } else if (novoTipo === "Diariamente") {
            setFormTipoAgendamento(<></>);
            const agendamentoDiario: IAgendamentoTarefa = {
                id: uuidv4(),
                tipo: novoTipo,
                horaExecucao: horaExecucao
            };
            setTarefa({ ...tarefa, agendamento: agendamentoDiario });
        } else if (novoTipo === "Repetidamente") {
            setFormTipoAgendamento(<AgendamentoRepetidamente />);
            const agendamentoRepetidamente: IAgendamentoRepedidamente = {
                id: uuidv4(),
                tipo: "Repetidamente",
                horaExecucao: horaExecucao,
                tipoIntervalo: (tarefa.agendamento as any)?.tipoIntervalo || 'Minuto',
                intervalo: (tarefa.agendamento as any)?.intervalo || 15
            };
            setTarefa({ ...tarefa, agendamento: agendamentoRepetidamente });            
        } else {
            setFormTipoAgendamento(<></>);
            const agendamentoUmaVez: IAgendamentoTarefa = {
                id: uuidv4(),
                tipo: "UmaVez",
                horaExecucao: horaExecucao
            };
            setTarefa({ ...tarefa, agendamento: agendamentoUmaVez });
        }

        setTipo(novoTipo);
    }

    return (
        <Box>
            <FormControl fullWidth>
                <BFInput
                    id="dataInicio"
                    name="dataInicio"
                    label="Data Início"
                    variant="outlined"
                    type="date"
                    onChange={handleDataInicioAlterada}
                    value={dataInicio}
                    InputLabelProps={{
                        shrink: true
                    }}
                    required />

                <BFInput
                    id="dataFim"
                    name="dataFim"
                    label="Data Fim"
                    variant="outlined"
                    type="date"
                    onChange={handleDataFimAlterada}
                    value={dataFim}
                    InputLabelProps={{
                        shrink: true
                    }}
                    required />

                <BFInput
                    id="horaExecucao"
                    name="horaExecucao"
                    label="Hora Execução"
                    variant="outlined"
                    type="time"
                    onChange={handleHoraExecucaoAlterado}
                    value={horaExecucao}
                    required
                    InputLabelProps={{
                        shrink: true
                    }}
                    sx={{ marginBottom: '16px' }} />
            </FormControl>

            <FormControl fullWidth>
                <InputLabel id="tipo-label">Frequência</InputLabel>
                <Select
                    labelId="tipo-label"
                    id="tipo"
                    value={tipo}
                    label="Frequência"
                    required
                    onChange={handleChangeTipo}
                >
                    <MenuItem value="Diariamente">Diariamente</MenuItem>
                    <MenuItem value="UmaVez">Uma vez</MenuItem>
                    <MenuItem value="Repetidamente">Repetidamente</MenuItem>
                    <MenuItem value="Semanalmente">Semanalmente</MenuItem>
                    <MenuItem value="Mensalmente">Mensalmente</MenuItem>
                </Select>
            </FormControl>

            {formTipoAgendamento}
        </Box>
    );
};

export default Agendamento;